import React, { useState, useEffect } from 'react';
import { API_URLS } from '../config/api';
import { apiGet, apiPost } from '../utils/apiUtils';
import { setToken } from '../utils/authUtils';
import { trackEvent } from '../utils/analytics';
import { HAS_TELEGRAM, IS_EUROPE, METRIKA_ID, localePath } from '../config/region';
import { t } from '../i18n';
import './AuthModal.css';

const AuthModal = ({
  onSuccess,
  onClose,
  initialMode = 'login',
  starBalanceMode = false,
  canGetRegistrationStars = false,
}) => {
  const [mode, setMode] = useState(initialMode); // 'login' | 'register' | 'forgot' | 'registered'
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  /* Consent to the Terms and the Privacy Policy is an explicit, unticked box rather than
     something the Create Account button implies. Under the GDPR consent has to be an
     unambiguous affirmative act, and a pre-ticked box does not qualify. */
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  /* Stars paid for confirming an email. Asked of the backend rather than assumed from the region:
     it is 0 whenever confirmation mail is not actually being sent, and advertising a reward that
     cannot be earned is worse than not mentioning it. Russia never asks and never shows it. */
  const [emailRewardStars, setEmailRewardStars] = useState(0);

  useEffect(() => {
    if (!IS_EUROPE) return;
    apiGet(API_URLS.EMAIL_REWARD, {}, false, '', true)
      .then((response) => setEmailRewardStars(response.data.stars || 0))
      .catch(() => {});
  }, []);

  const switchTo = (m) => { setMode(m); setAuthError(null); };

  /* ── Регистрация ─────────────────────────────────────── */
  const handleRegister = async (event) => {
    event.preventDefault();
    setAuthError(null);
    setAuthLoading(true);

    const formData = new FormData(event.target);
    const username = formData.get('username');
    const password = formData.get('password');
    const confirmPassword = formData.get('confirmPassword');
    const email = formData.get('email').trim().toLowerCase();

    if (!username || !password || !confirmPassword || !email) {
      setAuthError(t('Пожалуйста, заполните все поля'));
      setAuthLoading(false);
      return;
    }

    if (!acceptedTerms) {
      setAuthError(t('Примите Пользовательское соглашение и Политику конфиденциальности, чтобы продолжить'));
      setAuthLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setAuthError(t('Пароли не совпадают'));
      setAuthLoading(false);
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setAuthError(t('Имя пользователя может содержать только английские буквы, цифры и нижнее подчёркивание'));
      setAuthLoading(false);
      return;
    }

    // Basic email regex check on the frontend
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAuthError(t('Введите корректный email'));
      setAuthLoading(false);
      return;
    }

    try {
      await apiPost(
        API_URLS.USER_REGISTER,
        { username, password, email },
        {},
        true,
        emailRewardStars > 0
          ? t('Аккаунт создан! Подтвердите email по ссылке из письма и получите {0} звёзд.', emailRewardStars)
          : t('Аккаунт создан! Проверьте почту для подтверждения.')
      );

      if (typeof window.ym === 'function') {
        window.ym(METRIKA_ID, 'reachGoal', 'registration');
      }
      trackEvent('sign_up', { method: 'email' });

      // Auto-login after registration
      const authFormData = new FormData();
      authFormData.append('username', username);
      authFormData.append('password', password);

      const loginResponse = await apiPost(
        API_URLS.USER_LOGIN,
        authFormData,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
        true,
        t('Вход выполнен успешно!')
      );

      setToken(loginResponse.data.access_token);
      if (onSuccess) onSuccess();
      setTimeout(() => window.location.reload(), 200);
    } catch (err) {
      setAuthError(err.message || t('Ошибка регистрации'));
    } finally {
      setAuthLoading(false);
    }
  };

  /* ── Вход ────────────────────────────────────────────── */
  const handleLogin = async (event) => {
    event.preventDefault();
    setAuthError(null);
    setAuthLoading(true);

    const formData = new FormData(event.target);
    const username = formData.get('username');
    const password = formData.get('password');

    if (!username || !password) {
      setAuthError(t('Пожалуйста, заполните все поля'));
      setAuthLoading(false);
      return;
    }

    try {
      const authFormData = new FormData();
      authFormData.append('username', username);
      authFormData.append('password', password);

      const response = await apiPost(
        API_URLS.USER_LOGIN,
        authFormData,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
        true,
        t('Вход выполнен успешно!')
      );

      setToken(response.data.access_token);
      if (onSuccess) onSuccess();
    } catch (err) {
      setAuthError(err.message || t('Ошибка входа'));
    } finally {
      setAuthLoading(false);
    }
  };

  /* ── Восстановление пароля ───────────────────────────── */
  const handleForgot = async (event) => {
    event.preventDefault();
    setAuthError(null);
    setAuthLoading(true);

    const formData = new FormData(event.target);
    const login = formData.get('login').trim();

    if (!login) {
      setAuthError(t('Введите имя пользователя или email'));
      setAuthLoading(false);
      return;
    }

    try {
      await apiPost(
        API_URLS.REQUEST_PASSWORD_RESET,
        { login },
        {},
        false
      );
      switchTo('forgot-sent');
    } catch (err) {
      setAuthError(err.message || t('Ошибка при отправке письма'));
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <div className="am-overlay" onClick={onClose}>
      <div className="am-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Форма регистрации ─────────────────────────── */}
        {mode === 'register' && (
          <div className="am-form">
            <h2 className="am-title">{t('Создать аккаунт')}</h2>

            {canGetRegistrationStars && (
              <div className="am-promo">
                {HAS_TELEGRAM
                  ? t('⭐ Получите 100 звёзд за регистрацию + ещё 100 за привязку Telegram = 200 БЕСПЛАТНЫХ звёзд!')
                  : t('⭐ Получите 100 звёзд за регистрацию бесплатно!')}
              </div>
            )}

            {emailRewardStars > 0 && (
              <div className="am-promo">
                {t('📧 Подтвердите email после регистрации и получите {0} бесплатных звёзд!', emailRewardStars)}
              </div>
            )}

            {starBalanceMode && (
              <div className="am-star-notice">
                {t('Зарегистрируйтесь, чтобы продолжить игру!')}
              </div>
            )}

            {authError && <div className="am-error">{authError}</div>}

            <form onSubmit={handleRegister}>
              <input className="am-input" type="text" name="username" placeholder={t('Имя пользователя')} autoComplete="username" required disabled={authLoading} />
              <input className="am-input" type="email" name="email" placeholder={t('Email')} autoComplete="email" required disabled={authLoading} />
              <input className="am-input" type="password" name="password" placeholder={t('Пароль')} autoComplete="new-password" required disabled={authLoading} />
              <input className="am-input" type="password" name="confirmPassword" placeholder={t('Подтвердить пароль')} autoComplete="new-password" required disabled={authLoading} />
              <label className="am-consent-check">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  disabled={authLoading}
                />
                <span>
                  {t('Я принимаю')}{' '}
                  <a href={localePath('/terms')} target="_blank" rel="noreferrer">{t('Пользовательское соглашение')}</a>{' '}{t('и')}{' '}
                  <a href={localePath('/privacy')} target="_blank" rel="noreferrer">{t('Политику конфиденциальности')}</a>
                </span>
              </label>
              <div className="am-btn-row">
                <button type="submit" className="am-btn-primary" disabled={authLoading || !acceptedTerms}>
                  {authLoading ? t('Загрузка…') : t('Создать аккаунт')}
                </button>
                <button type="button" className="am-btn-cancel" onClick={onClose} disabled={authLoading}>
                  {t('Отменить')}
                </button>
              </div>
            </form>

            <div className="am-switch">
              <button type="button" className="am-switch-btn" onClick={() => switchTo('login')} disabled={authLoading}>
                {t('Уже есть аккаунт? Войдите')}
              </button>
            </div>
          </div>
        )}

        {/* ── Письмо отправлено после регистрации ──────── */}
        {mode === 'registered' && (
          <div className="am-form">
            <h2 className="am-title">{t('Проверьте почту')}</h2>
            <p className="am-info-text">
              {t('Аккаунт создан! Мы отправили письмо со ссылкой для подтверждения email.')}<br />
              {t('После подтверждения вы сможете войти в аккаунт.')}
            </p>
            <div className="am-btn-row">
              <button type="button" className="am-btn-primary" onClick={() => switchTo('login')}>
                {t('Войти')}
              </button>
              <button type="button" className="am-btn-cancel" onClick={onClose}>
                {t('Закрыть')}
              </button>
            </div>
          </div>
        )}

        {/* ── Форма входа ───────────────────────────────── */}
        {mode === 'login' && (
          <div className="am-form">
            <h2 className="am-title">{t('Вход в аккаунт')}</h2>

            {canGetRegistrationStars && (
              <div className="am-promo">
                {HAS_TELEGRAM
                  ? t('⭐ Получите 100 звёзд за регистрацию + ещё 100 за привязку Telegram = 200 БЕСПЛАТНЫХ звёзд!')
                  : t('⭐ Получите 100 звёзд за регистрацию бесплатно!')}
              </div>
            )}

            {starBalanceMode && (
              <div className="am-star-notice">
                {t('Чтобы продолжить, войдите в аккаунт или зарегистрируйтесь')}
              </div>
            )}

            {/* Out of stars and signed out is when the offer matters most, and this tab is the one
                PlayPage opens on. Worded as an invitation to sign up, not a promise to the
                reader: someone logging back in may have claimed it already. */}
            {starBalanceMode && emailRewardStars > 0 && (
              <div className="am-promo">
                {t('📧 Новый игрок? Зарегистрируйтесь и подтвердите email — получите {0} звёзд бесплатно!', emailRewardStars)}
              </div>
            )}

            {authError && <div className="am-error">{authError}</div>}

            <form onSubmit={handleLogin}>
              <input className="am-input" type="text" name="username" placeholder={t('Имя пользователя или email')} autoComplete="username" required disabled={authLoading} />
              <input className="am-input" type="password" name="password" placeholder={t('Пароль')} autoComplete="current-password" required disabled={authLoading} />
              <div className="am-btn-row">
                <button type="submit" className="am-btn-primary" disabled={authLoading}>
                  {authLoading ? t('Загрузка…') : t('Войти')}
                </button>
                <button type="button" className="am-btn-cancel" onClick={onClose} disabled={authLoading}>
                  {t('Отменить')}
                </button>
              </div>
            </form>

            <div className="am-switch">
              <button
                type="button"
                className={`am-switch-btn${starBalanceMode ? ' am-switch-btn--star' : ''}`}
                onClick={() => switchTo('register')}
                disabled={authLoading}
              >
                {t('Нужен аккаунт? Зарегистрируйтесь')}
              </button>
              <button type="button" className="am-switch-btn" onClick={() => switchTo('forgot')} disabled={authLoading}>
                {t('Забыли пароль?')}
              </button>
            </div>
          </div>
        )}

        {/* ── Форма восстановления пароля ───────────────── */}
        {mode === 'forgot' && (
          <div className="am-form">
            <h2 className="am-title">{t('Восстановление пароля')}</h2>
            <p className="am-info-text">
              {t('Введите ваше имя пользователя или email. Если аккаунт найден и email подтверждён — мы отправим ссылку для сброса пароля.')}
            </p>

            {authError && <div className="am-error">{authError}</div>}

            <form onSubmit={handleForgot}>
              <input className="am-input" type="text" name="login" placeholder={t('Имя пользователя или email')} autoComplete="username" required disabled={authLoading} />
              <div className="am-btn-row">
                <button type="submit" className="am-btn-primary" disabled={authLoading}>
                  {authLoading ? t('Отправка…') : t('Отправить письмо')}
                </button>
                <button type="button" className="am-btn-cancel" onClick={onClose} disabled={authLoading}>
                  {t('Отменить')}
                </button>
              </div>
            </form>

            <div className="am-switch">
              <button type="button" className="am-switch-btn" onClick={() => switchTo('login')} disabled={authLoading}>
                {t('Вернуться ко входу')}
              </button>
            </div>
          </div>
        )}

        {/* ── Письмо отправлено (сброс пароля) ─────────── */}
        {mode === 'forgot-sent' && (
          <div className="am-form">
            <h2 className="am-title">{t('Письмо отправлено')}</h2>
            <p className="am-info-text">
              {t('Письмо со ссылкой для сброса пароля отправлено на ваш email.')}<br />
              {t('Ссылка действительна 1 час.')}
            </p>
            <div className="am-btn-row">
              <button type="button" className="am-btn-primary" onClick={() => switchTo('login')}>
                {t('Войти')}
              </button>
              <button type="button" className="am-btn-cancel" onClick={onClose}>
                {t('Закрыть')}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AuthModal;

